import type { RecurrenceInput } from '../../api/calendar';
import { parseRecurrenceRule, recurrenceSignature, type RecurEnd, type RecurFreq } from './parseRecurrenceRule';
import { tCalCreate, type CalendarCreateI18nKey } from '../../lib/calendar-create-i18n';

/**
 * 반복 설정 창의 «상태 ↔ 서버 입력» (2026-10-13 이식 — 옛 가지 ee33d27d7·21f8b4e23·ec36d8a56·e9255f050).
 *
 * ⭐ 순수 함수만 둔다 — 화면(`RecurrenceSettings`)과 저장(`EventCreateModal`)이 «같은 셈»을 쓴다.
 *
 * ## ⭐⭐ `localStartDate` 를 늘 싣는다
 * 서버는 규칙을 UTC 로 전개한다. KST 00:00~08:59 는 UTC 로 «전날»이라, 화면의 요일·순번·날을 그대로 보내면
 * 하루 밀린다. 그래서 «현지 날짜»를 함께 보내고, 서버가 UTC 기준으로 옮겨 적는다
 * (chat-server `localizedRuleTokens` 머리말 — 그 표가 SSOT 다).
 */

export const RECUR_TABS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const;
/** RRULE 요일 글자 — `Date#getDay()` 순서(0 = 일). */
export const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type WeekdayCode = (typeof WEEKDAY_CODES)[number];

export interface RecurState {
  freq: RecurFreq;
  interval: number;
  /** 매주 탭의 요일 체크(현지 요일). */
  byDay: WeekdayCode[];
  /** 매월·매년 탭에서 「몇째 주 무슨 요일」 방식인가(아니면 날짜 방식). */
  byWeekday: boolean;
  end: RecurEnd;
  count: number;
  /** `<input type="date">` 의 현지 날짜(YYYY-MM-DD). */
  until: string;
  /** ⭐ 2026-10-14 (E-3) — 종료 「기간」의 N(반복 단위: 일·주·개월·년). */
  period: number;
}

export const EMPTY_RECUR: RecurState = {
  freq: '', interval: 1, byDay: [], byWeekday: false, end: 'never', count: 10, until: '', period: 10,
};

/**
 * ⭐ 2026-10-14 (반복 규칙 E-3 · 사용자 확정) — 종료 「기간」(N 단위 동안)의 마지막 날(현지 `YYYY-MM-DD`, 그 날 포함).
 *
 * ```
 * 끝 = 시작 현지 날짜 + N 단위 − 1일
 *   매일 N일 · 매주 N주(7N일) · 매월 N개월 · 매년 N년
 * ```
 * ⚠ 개월·년 더하기는 «없는 날»을 말일로 당긴다(1/31 + 1개월 = 2/28(윤년 2/29) → 끝 2/27).
 *   못 읽으면 null.
 */
export function periodEndDate(startDate: string, freq: RecurFreq, n: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate);
  if (!m || !freq || !Number.isFinite(n) || n < 1) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dim = (yy: number, mm: number) => new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  let end: number;
  if (freq === 'DAILY' || freq === 'WEEKLY') {
    end = Date.UTC(y, mo - 1, d + (freq === 'WEEKLY' ? 7 * n : n));
  } else {
    const addMonths = freq === 'MONTHLY' ? n : 12 * n;
    const total = (mo - 1) + addMonths;
    const ny = y + Math.floor(total / 12);
    const nm = (total % 12) + 1;
    end = Date.UTC(ny, nm - 1, Math.min(d, dim(ny, nm)));
  }
  const last = new Date(end - 86_400_000);
  return `${last.getUTCFullYear()}-${pad(last.getUTCMonth() + 1)}-${pad(last.getUTCDate())}`;
}

/** `YYYY-MM-DDTHH:MM`(datetime-local) → Date. 못 읽으면 null. */
function parseLocal(dtLocal: string): Date | null {
  const d = new Date(dtLocal);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 시작의 «현지 날짜» `YYYY-MM-DD` — 서버에 보내는 기준 날짜. */
export function localDateOf(dtLocal: string): string | null {
  const d = parseLocal(dtLocal);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : null;
}

export function weekdayCodeOf(dtLocal: string): WeekdayCode {
  const d = parseLocal(dtLocal) ?? new Date();
  return WEEKDAY_CODES[d.getDay()]!;
}

/**
 * 「매주」로 켠다 — 요일이 비어 있으면 시작 요일을 미리 고른다(빈 채로 두면 「아무 요일도 아닌」 반복이 된다).
 * 일정 창의 「반복」 체크와 반복 설정의 「매주」 탭이 같은 규칙을 쓴다.
 */
export function turnOnWeekly(s: RecurState, startLocal: string): RecurState {
  return { ...s, freq: 'WEEKLY', byDay: s.byDay.length > 0 ? s.byDay : [weekdayCodeOf(startLocal)] };
}

/** 그 날(1~31)이 그 달의 몇째 주인가(1~5). 5 = 다섯째 ⇒ 서버가 「마지막 주」로 적는다. */
export function nthOfDay(day: number): number {
  return Math.floor((day - 1) / 7) + 1;
}

/** 그 달의 몇째 주인가(1~5). 5 = 다섯째 ⇒ 서버가 「마지막 주」로 적는다. */
export function nthOfMonth(dtLocal: string): number {
  const d = parseLocal(dtLocal) ?? new Date();
  return nthOfDay(d.getDate());
}

/** 요일 글자 한 자(「화」·「Tu」…). */
export function weekdayText(code: WeekdayCode): string {
  return tCalCreate(`rc.weekday.${WEEKDAY_KEYS[WEEKDAY_CODES.indexOf(code)]}` as CalendarCreateI18nKey);
}

/*
 * ⭐ 매월·매년 문구 넷 — (월, 일, 요일, 몇째 주) 값으로 짓는다. 아래 «시작 날짜» 라벨(반복 설정 창)과
 *   「이후 일정」 미리보기(recurrence-future-rule `describeMovedRecurrence`)가 같은 문구를 쓴다.
 *   몇째 주는 5 이상 = 「마지막 주」(다섯째일 때만 — 넷째는 넷째 그대로다).
 */

/** 매월 날짜 방식 — 31 = 말일, 29~30 은 말일로 당긴다는 뜻을 함께. */
export function monthlyByDateText(day: number): string {
  if (day >= 31) return tCalCreate('rc.monthly_last_day');
  if (day >= 29) return tCalCreate('rc.monthly_by_date_clamp', { day });
  return tCalCreate('rc.monthly_by_date', { day });
}

/** 매월 「몇째 주 무슨 요일」. */
export function monthlyByWeekdayText(code: WeekdayCode, nth: number): string {
  const weekday = weekdayText(code);
  if (nth >= 5) return tCalCreate('rc.monthly_by_weekday_last', { weekday });
  return tCalCreate('rc.monthly_by_weekday', { nth: tCalCreate(`rc.nth.${nth}` as CalendarCreateI18nKey), weekday });
}

/** 매년 날짜 방식 — 2/29 는 평년 처리를 함께 말한다. */
export function yearlyByDateText(month: number, day: number): string {
  if (month === 2 && day === 29) return tCalCreate('rc.yearly_feb29');
  return tCalCreate('rc.yearly_by_date', { month, day });
}

/** 매년 「몇 월 몇째 주 무슨 요일」. */
export function yearlyByWeekdayText(month: number, code: WeekdayCode, nth: number): string {
  const weekday = weekdayText(code);
  if (nth >= 5) return tCalCreate('rc.yearly_by_weekday_last', { month, weekday });
  return tCalCreate('rc.yearly_by_weekday', { month, nth: tCalCreate(`rc.nth.${nth}` as CalendarCreateI18nKey), weekday });
}

/** 매월 탭 — 날짜 방식 라벨(29~31 은 말일로 당긴다는 뜻을 함께). */
export function monthlyByDateLabel(dtLocal: string): string {
  const d = parseLocal(dtLocal) ?? new Date();
  return monthlyByDateText(d.getDate());
}

/** 매월 탭 — 「몇째 주 무슨 요일」 라벨. */
export function monthlyByWeekdayLabel(dtLocal: string): string {
  const d = parseLocal(dtLocal) ?? new Date();
  return monthlyByWeekdayText(WEEKDAY_CODES[d.getDay()]!, nthOfDay(d.getDate()));
}

export function yearlyByDateLabel(dtLocal: string): string {
  const d = parseLocal(dtLocal) ?? new Date();
  return yearlyByDateText(d.getMonth() + 1, d.getDate());
}

export function yearlyByWeekdayLabel(dtLocal: string): string {
  const d = parseLocal(dtLocal) ?? new Date();
  return yearlyByWeekdayText(d.getMonth() + 1, WEEKDAY_CODES[d.getDay()]!, nthOfDay(d.getDate()));
}

/**
 * 화면 상태 → 서버 `RecurrenceInput`. 반복이 꺼져 있으면 undefined.
 *
 * ```
 * 매주  byday = 고른 요일(현지) — 하나도 안 고르면 시작 요일
 * 매월  날짜 방식  bymonthday = 시작의 날(서버가 29~31 을 말일로 당긴다)
 *       요일 방식  byday=[시작 요일] · bysetpos = 몇째 주(5 = 마지막)
 * 매년  날짜 방식  bymonth·bymonthday = 시작의 달·날(2/29 는 평년에 2/28)
 *       요일 방식  byday · bysetpos · bymonth
 * 모두  localStartDate = 시작의 현지 날짜
 * ```
 */
export function recurrenceInputFromState(s: RecurState, startLocal: string): RecurrenceInput | undefined {
  if (!s.freq) return undefined;
  const d = parseLocal(startLocal);
  const localStartDate = localDateOf(startLocal) ?? undefined;
  // 기간의 마지막 날 — 아래 until·period 두 칸이 같은 값을 보므로 한 번만 센다.
  const periodEnd = s.end === 'period' && localStartDate ? periodEndDate(localStartDate, s.freq, s.period) : null;
  const base: RecurrenceInput = {
    freq: s.freq,
    ...(s.interval > 1 ? { interval: s.interval } : {}),
    ...(s.end === 'count' ? { count: s.count } : {}),
    ...(s.end === 'until' && s.until ? { until: untilIso(s.until) } : {}),
    // ⭐ 2026-10-14 (E-3) — 기간은 «종료일»로 보낸다(서버 무변경 — 날짜 종료와 같은 길).
    ...(periodEnd ? { until: untilIso(periodEnd) } : {}),
    // ⭐ 2026-10-14 (E-3 보강) — N 도 함께 — 서버가 새 시리즈 시작부터 다시 센다(위 until 은 연 회차 기준이라 「전체」에서 어긋난다).
    ...(periodEnd ? { period: s.period } : {}),
    ...(localStartDate ? { localStartDate } : {}),
  };
  if (s.freq === 'WEEKLY') {
    const days = WEEKDAY_CODES.filter((c) => s.byDay.includes(c));
    return { ...base, byday: days.length > 0 ? days : [weekdayCodeOf(startLocal)] };
  }
  if ((s.freq === 'MONTHLY' || s.freq === 'YEARLY') && d) {
    if (s.byWeekday) {
      return {
        ...base,
        byday: [weekdayCodeOf(startLocal)],
        bysetpos: Math.min(5, nthOfMonth(startLocal)),
        ...(s.freq === 'YEARLY' ? { bymonth: d.getMonth() + 1 } : {}),
      };
    }
    return {
      ...base,
      bymonthday: d.getDate(),
      ...(s.freq === 'YEARLY' ? { bymonth: d.getMonth() + 1 } : {}),
    };
  }
  return base;
}

/**
 * 종료 날짜(현지 `YYYY-MM-DD`) → UNTIL ISO. ⭐ 그 날 «끝»(23:59:59 현지)까지 포함한다.
 * ⚠ 종전(`new Date('YYYY-MM-DD')`)은 UTC 자정이라 KST 09:00 이후 회차가 그 날에서 빠졌다.
 */
export function untilIso(dateOnly: string): string {
  const d = new Date(`${dateOnly}T23:59:59`);
  return Number.isNaN(d.getTime()) ? new Date(dateOnly).toISOString() : d.toISOString();
}

/**
 * 저장된 규칙 → 화면 상태. 수정 화면이 «지금 설정»을 채울 때 쓴다.
 * ⚠ 매주 요일은 서버가 «UTC 기준»으로 적었을 수 있다 — `parseRecurrenceRule` 이 시작 시각으로 되돌린다.
 */
export function recurStateFromRule(rule: string | null | undefined, startIso: string | null | undefined): RecurState {
  const p = parseRecurrenceRule(rule, startIso ?? undefined);
  if (!p) return { ...EMPTY_RECUR };
  return {
    freq: p.freq,
    interval: p.interval,
    byDay: p.byDay.filter((c): c is WeekdayCode => (WEEKDAY_CODES as readonly string[]).includes(c)),
    byWeekday: p.byWeekday,
    end: p.end,
    count: p.count,
    until: p.until,
    period: EMPTY_RECUR.period,
  };
}

/** 화면에 보이는 축으로만 «바뀌었나»를 잰다. */
export function recurSignatureOf(s: RecurState): string {
  return recurrenceSignature(s);
}

/** 규칙에 «날짜로 정한» 조각(몇째 주·말일·2/29)이 있나 — 날짜를 옮기면 규칙도 새로 적어야 한다. */
export function ruleHasDateParts(rule: string | null | undefined): boolean {
  if (!rule) return false;
  return /(^|;)(BYSETPOS|BYMONTHDAY|BYMONTH|BYYEARDAY|BYWEEKNO)=/i.test(rule.replace(/^RRULE:/i, ''));
}
